const ACTIVATION_MEANS = new Map([
  ['Supplier', 'Receive POs and payments'],
  ['Employee', 'System access + payroll'],
  ['Intern', 'System access + stipend'],
  ['Partner', 'Sales portal + commission'],
  ['Merchant', 'List + receive payouts'],
  ['Client', 'Client workspace access']
]);

const DEFAULT_ACTIVATION_DECISION = {
  decision: 'Pending',
  decision_tone: 'warning',
  action: 'Wait',
  action_tone: 'plain'
};

function normalize_text(value = '') {
  return String(value || '').trim().toLowerCase();
}

function matches_active_journey(case_item = {}, journey_filter = '') {
  const normalized_filter = normalize_text(journey_filter);
  if (!normalized_filter) return true;

  return [
    case_item?.entity_type_id,
    case_item?.journey_tone,
    case_item?.journey_key,
    case_item?.journey
  ].some((value) => normalize_text(value) === normalized_filter);
}

function tab_matches_profile(tab = {}) {
  const label = normalize_text(`${tab?.id || ''} ${tab?.label || ''}`);
  return ['profile', 'personal', 'applicant'].some((word) => label.includes(word));
}

function pick_reference_tab(tabs = []) {
  return tabs.find(tab_matches_profile) || tabs[0] || null;
}

export const ACTIVATION_STATE = {
  is_loading_queue: false,
  is_loading_detail: false,
  queue_error: '',
  queue: [],
  selected_activation: null,
  selected_case: null,
  active_detail_tab: 'summary',
  is_detail_fullscreen: false,
  detail_tabs: [
    { id: 'summary', label: 'Required Action: Activation Review', icon: 'bi-grid', badge: 'Current' }
  ],
  summary_rows: [],
  stage_timeline: [],
  case_form_sections: [],
  outbound_items: [
    {
      icon: 'bi-archive',
      title: 'Evidence pack',
      detail: 'All documents collected to pass gates',
      target: 'Evidence vault'
    },
    {
      icon: 'bi-shield-check',
      title: 'Control satisfactions',
      detail: 'Which Complyon controls this onboarding has satisfied',
      target: 'Controls library'
    },
    {
      icon: 'bi-exclamation-triangle',
      title: 'Risk score baseline',
      detail: 'Score + reasons, captured at intake',
      target: 'Risk register'
    },
    {
      icon: 'bi-hammer',
      title: 'Approval history',
      detail: 'Every approver, every decision, every timestamp',
      target: 'DoA audit trail'
    },
    {
      icon: 'bi-check-circle',
      title: 'Activation decision',
      detail: 'Activated by, when, under which template version',
      target: 'Audit log'
    }
  ],
  inbound_items: [
    {
      icon: 'bi-hammer',
      title: 'DoA thresholds',
      detail: 'Which approvals are required at which value/risk',
      source: 'Delegation of authority'
    },
    {
      icon: 'bi-shield',
      title: 'Control requirements',
      detail: 'What evidence each journey must collect',
      source: 'Controls library'
    },
    {
      icon: 'bi-file-earmark-text',
      title: 'Policy rules',
      detail: 'Mandatory training, conflict declarations, retention',
      source: 'Policies'
    },
    {
      icon: 'bi-arrow-counterclockwise',
      title: 'Re-onboarding call',
      detail: 'Trigger a new onboarding when annual refresh is due',
      source: 'Supplier register Â· calendar'
    }
  ]
};

export const manageActivationComputed = {
  active_journey_filter() {
    return this.resolve_onboarding_journey_filter_key(this.$route);
  },
  table_total_rows() {
    return this.filtered_queue.length;
  },
  filtered_queue() {
    return this.filter_table_rows(this.queue);
  },
  paginated_queue() {
    return this.paginate_table_rows(this.filtered_queue);
  },
  ready_count() {
    return this.queue.filter((item) => item.decision_tone === 'success').length;
  },
  active_form_section() {
    const forms = Array.isArray(this.case_form_sections) ? this.case_form_sections : [];
    for (const form of forms) {
      const matched_section = (Array.isArray(form?.sections) ? form.sections : [])
        .find((section) => this.tab_id_for_section(form, section) === this.active_detail_tab);

      if (matched_section) {
        return matched_section;
      }
    }

    return null;
  }
};

export function get_activation_means(journey) {
  return ACTIVATION_MEANS.get(journey) || 'Activate onboarding record';
}

export function get_activation_decision(case_item = {}) {
  return [
    [case_item.activation_tone === 'ready' || case_item.progress >= 90, { decision: 'Ready', decision_tone: 'success', action: 'Activate', action_tone: 'success' }],
    [case_item.activation_tone === 'blocked', { decision: 'Blocked', decision_tone: 'danger', action: 'Resolve evidence', action_tone: 'link' }]
  ].find(([matches]) => matches)?.[1] || {
    ...DEFAULT_ACTIVATION_DECISION,
    decision: case_item.activation || DEFAULT_ACTIVATION_DECISION.decision
  };
}

export function map_case_to_activation_item(case_item = {}) {
  return {
    id: case_item.application_id || case_item.id || case_item.name,
    case_name: case_item.name,
    journey: case_item.journey,
    activation_means: get_activation_means(case_item.journey),
    readiness: `${case_item.progress}%`,
    ...get_activation_decision(case_item),
    case_item
  };
}

export function filter_activation_cases(cases = [], active_journey_filter = '') {
  return cases
    .filter((case_item) => matches_active_journey(case_item, active_journey_filter))
    .map((case_item) => map_case_to_activation_item(case_item))
    .sort((first, second) => Number.parseInt(second.readiness, 10) - Number.parseInt(first.readiness, 10));
}

export function decision_badge_class(tone) {
  if (tone === 'danger') return 'bg-danger-subtle text-danger-emphasis';
  if (tone === 'success') return 'bg-success-subtle text-success-emphasis';
  if (tone === 'warning') return 'bg-warning-subtle text-warning-emphasis';
  return 'bg-secondary-subtle text-secondary-emphasis';
}

export function action_class(tone) {
  if (tone === 'success') return 'text-success fw-semibold';
  if (tone === 'link') return 'fw-semibold';
  return 'text-black';
}

export function tab_id_for_section(form = {}, section = {}) {
  return `form-${form?.id || form?.form_name || 'section'}-${section?.id || section?.label || 'item'}`;
}

export function tab_icon_for_section(form = {}, section = {}) {
  const label = normalize_text(`${form?.form_name || ''} ${section?.label || ''}`);
  return ['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => label.includes(word))
    ? 'bi-folder'
    : 'bi-list-check';
}

export function build_detail_tabs(form_sections = []) {
  const section_tabs = [];
  form_sections.forEach((form) => {
    (Array.isArray(form?.sections) ? form.sections : []).forEach((section) => {
      section_tabs.push({
        id: tab_id_for_section(form, section),
        label: section?.label || form?.form_name || 'Section',
        icon: tab_icon_for_section(form, section)
      });
    });
  });

  const reference_tab = pick_reference_tab(section_tabs);
  const tabs = [{
    id: 'summary',
    label: 'Required Action: Activation Review',
    icon: 'bi-grid',
    badge: 'Current'
  }];
  if (reference_tab) {
    tabs.push({
      ...reference_tab,
      label: 'Application Reference',
      badge: 'Reference'
    });
  }

  section_tabs
    .filter((tab) => tab.id !== reference_tab?.id)
    .forEach((tab) => {
      tabs.push(tab);
    });
  return tabs;
}

export function selected_case_subtitle(selected_case = null, selected_activation = null) {
  if (!selected_case) return '';

  return [
    selected_case?.detail,
    selected_case?.activation,
    selected_case?.eta
  ].filter(Boolean).join(' Â· ') || selected_activation?.activation_means || '';
}

export function progress_bar_color(selected_case = null) {
  return selected_case?.progress_tone === 'success' ? '#198754' : '#bf4b08';
}
